// src/newDashboard/shared/pillCatalog.js
//
// Every pill (event type, amenity) the dashboard offers, and which states
// the regional ones belong to. A pill NOT in DEFAULT_PILL_STATES shows
// everywhere. The admin "Pills by state" page saves changes to Firestore
// (config/pillStates), and those changes win over the defaults here.
//
// The customer app has the same lists in lib/services/pill_catalog.dart.
// If you add a pill, add it in both places.

import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";

export const GAME_DAY = "Game Day Special";

// Renamed pills: old saved events still count as the new name.
export const OLD_PILL_NAMES = {
  "Vikings/Packers Game Special": GAME_DAY,
  "Vikings Game Specials": GAME_DAY,
};
export const canonicalPill = (p) => OLD_PILL_NAMES[p] || p;

export const EVENT_CATEGORY_OPTIONS = ["Gambling", "Entertainment", "Sports", "Other", "Local Favorites"];

export const EVENT_SUBCATEGORY_OPTIONS = {
  Gambling: ["Bingo", "Meat Raffle", "Paddlewheel", "Poker", "Shake-a-Day"],
  Entertainment: [
    "Live Band", "DJ", "Karaoke", "Trivia", "Open Mic", "Comedy Night", "Drag Show", "Music Bingo",
    "Dance Night", "Dueling Pianos", "Polka Night", "Two-Step Night", "Honky-Tonk Night",
    "Zydeco Night", "Mariachi Night", "Bluegrass Night",
  ],
  Sports: [GAME_DAY, "UFC Fight", "Pay-Per-View Event"],
  Other: [
    "Ladies Night", "College Night", "Industry Night", "Dart League", "Pool Tournament",
    "Cornhole Tournament", "Card Tournament", "League Night", "Fish Fry", "Food Night", "Tasting",
    "Fundraiser",
  ],
  "Local Favorites": [
    "Supper Club", "Crawfish Boil", "Shrimp Boil", "Low Country Boil", "Oyster Night", "Crab Feast",
    "Lobster Night", "Clam Bake", "BBQ Night", "Green Chile Night", "Bourbon Tasting", "Derby Party",
    "Mardi Gras Party", "Après-Ski", "Tiki Night",
  ],
};

export const AMENITY_GROUPS = {
  Games: [
    "Darts", "Pool Tables", "Golden Tee", "Cornhole / Bags",
    "Arcade Games", "Buck Hunter", "Golf Simulator", "Axe Throwing",
    "Mini Bowling", "Go-Karts", "E-tabs", "Pull-tabs", "Jukebox / TouchTunes",
  ],
  Amenities: [
    "Outdoor Patio", "Full Kitchen", "Late-Night Food",
    "Family Friendly", "Dog Friendly",
  ],
  "Outdoors & Views": [
    "Rooftop", "Fire Pit", "Heated Patio", "Waterfront", "Ocean View Patio",
    "Beach Bar", "Boat Access", "Snowmobile Trail Access",
  ],
};

export const STATES = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"],
  ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"],
  ["DC", "District of Columbia"], ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"],
  ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"],
  ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"],
  ["MD", "Maryland"], ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"],
  ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"],
  ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"],
  ["NY", "New York"], ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"],
  ["OK", "Oklahoma"], ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"],
  ["SC", "South Carolina"], ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"],
  ["UT", "Utah"], ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"],
  ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"],
];

// ---- Groups of states ------------------------------------------------------
const UPPER_MIDWEST = ["MN", "WI", "IA", "ND", "SD", "MI"];
const COAST = [
  "ME", "NH", "MA", "RI", "CT", "NY", "NJ", "DE", "MD", "DC", "VA", "NC", "SC", "GA", "FL", "AL",
  "MS", "LA", "TX", "CA", "OR", "WA", "HI", "AK",
];
const COUNTRY = ["TX", "OK", "TN", "KY", "AL", "GA", "MS", "AR", "LA", "MO", "KS", "NE", "WY", "MT", "CO", "NM", "AZ"];
const SOUTHWEST = ["TX", "AZ", "NM", "CA", "NV", "CO"];
const BBQ = ["TX", "OK", "KS", "MO", "TN", "NC", "SC", "AL", "GA", "AR", "KY", "MS"];
const BOURBON = ["KY", "TN", "IN", "OH"];
const GULF = ["TX", "LA", "MS", "AL", "FL"];
const NEW_ENGLAND = ["ME", "NH", "VT", "MA", "RI", "CT", "NY"];
const SKI = ["CO", "UT", "MT", "WY", "ID", "NM", "NV", "CA", "OR", "WA", "AK", "VT", "NH", "ME", "NY"];
const SNOW = ["MN", "WI", "MI", "ND", "SD", "IA", "ME", "NH", "VT", "NY", "PA", "ID", "MT", "WY", "CO", "UT", "AK"];

export const DEFAULT_PILL_STATES = {
  "Meat Raffle": UPPER_MIDWEST,
  "Paddlewheel": ["MN", "WI", "ND"],
  "Shake-a-Day": ["WI", "MN", "IA", "MI"],
  "E-tabs": ["MN", "ND"],
  "Pull-tabs": [...UPPER_MIDWEST, "OH", "IN", "KY", "NE", "IL"],
  "Pull-tab / E-tab Updates": [...UPPER_MIDWEST, "OH", "IN", "KY", "NE", "IL"],
  "Polka Night": [...UPPER_MIDWEST, "IL", "OH", "PA", "NE", "TX"],
  "Two-Step Night": COUNTRY,
  "Honky-Tonk Night": COUNTRY,
  "Zydeco Night": ["LA", "TX", "MS"],
  "Mariachi Night": SOUTHWEST,
  "Bluegrass Night": ["KY", "TN", "NC", "VA", "WV"],
  "Supper Club": ["WI", "MN", "IA", "MI", "IL"],
  "Crawfish Boil": [...GULF, "GA", "SC", "AR"],
  "Shrimp Boil": [...GULF, "GA", "SC", "NC"],
  "Low Country Boil": ["SC", "GA", "NC", "FL"],
  "Oyster Night": COAST,
  "Crab Feast": ["MD", "DE", "VA", "DC", "NJ", "PA"],
  "Lobster Night": NEW_ENGLAND,
  "Clam Bake": [...NEW_ENGLAND, "NJ"],
  "BBQ Night": BBQ,
  "Green Chile Night": ["NM", "CO", "AZ", "TX"],
  "Bourbon Tasting": BOURBON,
  "Derby Party": BOURBON,
  "Mardi Gras Party": GULF,
  "Après-Ski": SKI,
  "Tiki Night": [...COAST, "AZ", "NV"],
  "Ocean View Patio": COAST,
  "Beach Bar": [...COAST, "MI", "WI", "MN", "OH", "IL"],
  "Snowmobile Trail Access": SNOW,
};

// Every pill name, for the admin page.
export const ALL_PILL_GROUPS = {
  ...EVENT_SUBCATEGORY_OPTIONS,
  Amenities: [...AMENITY_GROUPS.Games, ...AMENITY_GROUPS.Amenities, ...AMENITY_GROUPS["Outdoors & Views"]],
};

/** Defaults + the admin's saved changes. Empty list = everywhere. */
export function mergePillStates(saved) {
  return { ...DEFAULT_PILL_STATES, ...(saved || {}) };
}

/** Does [pill] belong in [state]? No state known = yes. */
export function pillAllowed(pill, state, pillStates) {
  const only = (pillStates || DEFAULT_PILL_STATES)[canonicalPill(pill)];
  const st = String(state || "").trim().toUpperCase();
  if (!only || only.length === 0 || st.length !== 2) return true;
  return only.includes(st);
}

/** Loads config/pillStates once and returns the merged map. */
let cached = null;
export function usePillStates() {
  const [map, setMap] = useState(cached || DEFAULT_PILL_STATES);
  useEffect(() => {
    if (cached) return;
    let alive = true;
    getDoc(doc(db, "config", "pillStates"))
      .then((snap) => {
        cached = mergePillStates(snap.exists() ? snap.data().pills : null);
        if (alive) setMap(cached);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return map;
}

/** After the admin saves, make every page use the new lists. */
export function setCachedPillStates(map) {
  cached = map;
}
